import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useContest, useTheme } from '../../../contexts/ContestContext';
import Blocks from '../../blocks/Blocks';
import StyledButton from '../../buttons/StyledButton';
import AccountDefault from '../../pages/AccountDefault';
import PageTitle from '../../partials/PageTitle';

import participationsImg from '../../../../img/coorslive/participations_image.png';
import styles from '../../../../styles/themes/coorslive/account-coorslive.module.css';

interface AccountCoorsliveProps {
    page: AccountPage | null;
    drawsSection?: React.ReactNode;
    prizesSection?: React.ReactNode;
    className: string;
}

const AccountCoorslive = ({ page = null, className = null, ...props }: AccountCoorsliveProps) => {
    const { locale } = useIntl();
    const { title = null, description = null, blocks = null } = page || {};
    const { stats: userStats = null } = useUser();
    const { participations = null } = userStats || {};
    const { handle = null } = useContest();
    const { isVariant = () => false } = useTheme();

    const isNoPinContest = useMemo(() => handle?.endsWith('_nopin'), [handle]);

    const finalParticipationsCount = useMemo(() => {
        return `${participations}`.padStart(2, '0');
    }, [participations]);

    const isNormandin = isVariant('normandin');
    if (isNormandin) {
        return <AccountDefault page={page} className={className} {...props} />;
    }

    return (
        <div className={classNames(styles.container, className)}>
            <div className={styles.header}>
                <PageTitle className={styles.pageTitle} title={title} />
                {description !== null ? (
                    <div className={styles.description}>{description}</div>
                ) : null}
            </div>

            <div className={classNames(styles.participationsCard)}>
                <img src={participationsImg} alt="" />
                <div className={classNames(styles.inner)}>
                    <div className={styles.label}>
                        <FormattedMessage
                            defaultMessage="Number of entries for the draw"
                            description="Coors Live account"
                        />
                    </div>
                    <div className={styles.counter}>{finalParticipationsCount}</div>
                </div>
            </div>

            {!isNoPinContest ? (
                <StyledButton
                    href={locale === 'fr' ? '/fr/prix' : '/en/prizes'}
                    className={styles.prizesButton}
                >
                    <FormattedMessage
                        defaultMessage="See all prizes"
                        description="Coorslive - Account"
                    />
                </StyledButton>
            ) : null}

            {blocks !== null ? <Blocks blocks={blocks} /> : null}
        </div>
    );
};

export default AccountCoorslive;
